import { useState, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useDemoModal } from "../contexts/DemoModalContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaBars,
  FaTimes,
  FaPhoneAlt,
  FaEnvelope,
  FaChevronDown,
  FaUsers,
  FaBriefcase,
} from "react-icons/fa";
import Flag from "react-world-flags";

const menuItems = [
  { label: "Home", path: "/" },
  {
    label: "What We Offer",
    path: "/courses",
    hasDropdown: true,
    dropdownType: "language",
  },
  { label: "Services", path: "/services" },
  {
    label: "Who We Are",
    path: "/who-we-are",
    hasDropdown: true,
    dropdownType: "whoWeAre",
  },
  { label: "Blogs", path: "/blogs" },
  { label: "Contact", path: "/contact" },
];

const languageDropdown = [
  { label: "French", path: "/french", flagCode: "FR" },
  { label: "German", path: "/german", flagCode: "DE" },
  { label: "Spanish", path: "/spanish", flagCode: "ES" },
  { label: "Japanese", path: "/japanese", flagCode: "JP" },
];

const whoWeAreDropdown = [
  { label: "About Us", path: "/who-we-are", icon: <FaUsers /> },
  { label: "Career", path: "/career", icon: <FaBriefcase /> },
  // { label: 'Testimonials', path: '/testimonials', icon: <FaStar /> },
];

const announcementItems = [
  "New Year Offer: Flat 30% OFF on All Courses",
  "Batches Starting Soon – Enroll Today !",
];

const Header = () => {
  const { openModal } = useDemoModal();
  const { settings } = useSiteSettings();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);

  const phoneLink = useMemo(() => {
    const number = settings.phone?.replace(/[^0-9+]/g, "") || "+919049830590";
    return `tel:${number}`;
  }, [settings.phone]);

  const emailLink = useMemo(() => {
    return `mailto:${settings.email || "enquiry@thelinguaalliance.com"}`;
  }, [settings.email]);

  const toggleMobileNav = () => {
    setIsMobileNavOpen((prev) => !prev);
  };

  const closeMobileNav = () => {
    setIsMobileNavOpen(false);
  };

  const handleSubmenuClick = () => {
    setIsMobileNavOpen(false);
    setHoveredNavItem(null);
  };

  return (
    <>
      {/* Top Announcement Bar - Not Sticky */}
      <div className="bg-gradient-to-r from-primary to-indigo-950 text-[0.85rem] text-white shadow-md">
        <div
          className="mx-auto flex w-full items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 py-0.5 lg:px-10"
          role="status"
          aria-live="polite"
        >
          {/* Left: Contact Info */}
          <div className="flex flex-shrink-0 items-center gap-2 sm:gap-4 whitespace-nowrap text-[0.7rem] sm:text-[0.8rem]">
            <a
              className="inline-flex items-center gap-1 sm:gap-2 font-medium"
              href={phoneLink}
            >
              <FaPhoneAlt className="text-white/80 text-xs sm:text-sm" />
              <span className="hidden xs:inline">{settings.phone}</span>
            </a>
            <span className="hidden h-3 w-px bg-white/30 sm:inline" />
            <a
              className="hidden items-center gap-2 font-medium lg:inline-flex"
              href={emailLink}
            >
              <FaEnvelope className="text-white/80" />
              <span>{settings.email}</span>
            </a>
          </div>

          {/* Center: Scrolling Announcements */}
          <div className="relative flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-10 whitespace-nowrap py-2 animate-announcement-scroll">
              {[...announcementItems, ...announcementItems].map(
                (item, index) => (
                  <span
                    key={`${item}-${index}`}
                    className="inline-flex items-center gap-2 px-4"
                  >
                    <span className="text-white/70">•</span>
                    <span>{item}</span>
                  </span>
                )
              )}
            </div>
          </div>

          {/* Right: Social Icons */}
          <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3 lg:gap-4 text-sm sm:text-base lg:text-lg text-white">
            <a
              href="https://www.instagram.com/the_lingua_alliance/"
              target="_blank" rel="noopener noreferrer"
              aria-label="Instagram"
              className="transition hover:text-primary-soft"
            >
              <FaInstagram />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61583176468896"
              target="_blank" rel="noopener noreferrer"
              aria-label="Facebook"
              className="transition hover:text-primary-soft"
            >
              <FaFacebookF />
            </a>
            <a
              href="https://www.linkedin.com/company/the-lingua-alliance/"
              target="_blank" rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="transition hover:text-primary-soft"
            >
              <FaLinkedinIn />
            </a>
            <a
              href="https://www.youtube.com/@TheLinguaAlliance"
              target="_blank" rel="noopener noreferrer"
              aria-label="YouTube"
              className="transition hover:text-primary-soft"
            >
              <FaYoutube />
            </a>
          </div>
        </div>
      </div>

      {/* Sticky Navigation Bar */}
      <header className="sticky top-0 left-0 right-0 z-50 bg-white shadow-lg shadow-indigo-100/40 w-full">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-3 sm:px-4 lg:px-6 py-1">
          <NavLink
            to="/"
            className="inline-flex items-center gap-2"
            onClick={closeMobileNav}
          >
            <img
              src={`${process.env.PUBLIC_URL}/tla-logo.png`}
              alt="The Lingua Alliance"
              className="h-[60px] sm:h-[70px] lg:h-[80px] w-auto transition-transform duration-300 scale-125 sm:scale-150"
            />
          </NavLink>

          <nav
            className={`hidden items-center gap-8 lg:flex ${
              isMobileNavOpen ? "text-primary" : ""
            }`}
          >
            {menuItems.map((item) => (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={() =>
                  item.hasDropdown && setHoveredNavItem(item.path)
                }
                onMouseLeave={() => setHoveredNavItem(null)}
              >
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    [
                      "group relative inline-flex items-center gap-1 text-[0.95rem] font-medium tracking-wide text-ink/80 transition-colors after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:bg-gradient-to-r after:from-primary after:to-primary-soft after:transition-transform",
                      isActive
                        ? "text-primary after:scale-x-100"
                        : "hover:text-primary after:group-hover:scale-x-100",
                    ].join(" ")
                  }
                  onClick={closeMobileNav}
                  end={item.path === "/"}
                >
                  {item.label}
                  {item.hasDropdown && (
                    <FaChevronDown
                      className="text-xs transition-transform duration-200"
                      style={{
                        transform:
                          hoveredNavItem === item.path
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                      }}
                    />
                  )}
                </NavLink>
                {item.hasDropdown && hoveredNavItem === item.path && (
                  <div
                    className="absolute top-full left-0 pt-2 z-50"
                    style={{ width: "224px" }}
                    onMouseEnter={() => setHoveredNavItem(item.path)}
                    onMouseLeave={() => setHoveredNavItem(null)}
                  >
                    <div className="rounded-2xl border border-primary/10 bg-white shadow-[0_20px_40px_rgba(18,10,56,0.18)] py-2 animate-fade-in">
                      {item.dropdownType === "language" &&
                        languageDropdown.map((lang) => (
                          <NavLink
                            key={lang.path}
                            to={lang.path}
                            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-ink/80 transition hover:bg-primary/5 hover:text-primary"
                            onClick={handleSubmenuClick}
                          >
                            <div className="flex h-5 w-8 items-center justify-center overflow-hidden rounded border border-primary/20">
                              <Flag
                                code={lang.flagCode}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <span>{lang.label}</span>
                          </NavLink>
                        ))}
                      {item.dropdownType === "whoWeAre" &&
                        whoWeAreDropdown.map((item) => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-ink/80 transition hover:bg-primary/5 hover:text-primary"
                            onClick={handleSubmenuClick}
                          >
                            <div className="flex h-5 w-5 items-center justify-center text-primary">
                              {item.icon}
                            </div>
                            <span>{item.label}</span>
                          </NavLink>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* Download Brochure Button - Desktop */}
            <button
              onClick={() => openModal()}
              className="hidden lg:inline-flex group relative items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-primary via-primary-soft to-primary px-6 py-2.5 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_8px_20px_rgba(79,64,198,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:scale-105 hover:shadow-[0_12px_30px_rgba(79,64,198,0.5)]"
            >
              <span className="relative z-10">Book free Demo</span>
              <span className="pointer-events-none absolute inset-0 rounded-full bg-white/20 opacity-0 mix-blend-overlay transition-opacity duration-300 group-hover:opacity-100" />
              <span className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            </button>

            {/* Download Brochure Button - Mobile (visible on larger mobile screens) */}
            <button
              onClick={() => openModal()}
              className="hidden sm:inline-flex lg:hidden group relative items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-primary via-primary-soft to-primary px-5 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-[0_8px_20px_rgba(79,64,198,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:scale-105 hover:shadow-[0_12px_30px_rgba(79,64,198,0.5)]"
            >
              <span className="relative z-10">Book free Demo</span>
              <span className="pointer-events-none absolute inset-0 rounded-full bg-white/20 opacity-0 mix-blend-overlay transition-opacity duration-300 group-hover:opacity-100" />
            </button>

            <button
              className="inline-flex items-center justify-center rounded-full border border-primary px-3 py-2 text-primary shadow-sm transition hover:bg-primary hover:text-white lg:hidden"
              type="button"
              onClick={toggleMobileNav}
              aria-expanded={isMobileNavOpen}
              aria-label="Toggle navigation"
            >
              {isMobileNavOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        <nav
          className={`lg:hidden ${
            isMobileNavOpen ? "max-h-[calc(100vh-160px)] opacity-100" : "max-h-0 opacity-0"
          } overflow-y-auto border-t border-slate-100 bg-white shadow-lg shadow-indigo-100/40 transition-all duration-300`}
        >
          <div className="flex flex-col gap-3 px-4 py-6 text-base font-medium text-ink/80">
            {menuItems.map((item) => (
              <div key={`mobile-${item.path}`}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 transition ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-primary/5 hover:text-primary"
                    }`
                  }
                  onClick={closeMobileNav}
                  end={item.path === "/"}
                >
                  {item.label}
                </NavLink>
                {item.hasDropdown && (
                  <div className="ml-4 mt-2 space-y-2 border-l-2 border-primary/20 pl-4">
                    {item.dropdownType === "language" &&
                      languageDropdown.map((lang) => (
                        <NavLink
                          key={`mobile-${lang.path}`}
                          to={lang.path}
                          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm transition hover:bg-primary/5 hover:text-primary"
                          onClick={handleSubmenuClick}
                        >
                          <div className="flex h-4 w-6 items-center justify-center overflow-hidden rounded border border-primary/20">
                            <Flag
                              code={lang.flagCode}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <span>{lang.label}</span>
                        </NavLink>
                      ))}
                    {item.dropdownType === "whoWeAre" &&
                      whoWeAreDropdown.map((item) => (
                        <NavLink
                          key={`mobile-${item.path}`}
                          to={item.path}
                          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm transition hover:bg-primary/5 hover:text-primary"
                          onClick={handleSubmenuClick}
                        >
                          <div className="flex h-4 w-4 items-center justify-center text-primary">
                            {item.icon}
                          </div>
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                  </div>
                )}
              </div>
            ))}

            {/* Download Brochure Button - Mobile Nav */}
            <button
              onClick={() => { openModal(); closeMobileNav(); }}
              className="group relative mt-4 inline-flex w-full items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-primary via-primary-soft to-primary px-6 py-1 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_8px_20px_rgba(79,64,198,0.35)] transition-all duration-300 active:scale-95"
            >
              <span className="relative z-10">Book free Demo</span>
              <span className="pointer-events-none absolute inset-0 rounded-full bg-white/20 opacity-0 mix-blend-overlay transition-opacity duration-300 group-active:opacity-100" />
              <span className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            </button>

            <div className="mt-4 grid gap-3 rounded-lg bg-primary/5 p-4 text-sm text-ink">
              <div className="flex items-center gap-2 text-primary font-semibold uppercase tracking-wide text-xs">
                Stay Connected
              </div>
              <a
                className="flex items-center gap-2 font-medium"
                href={phoneLink}
              >
                <FaPhoneAlt className="text-primary" />
                {settings.phone}
              </a>
              <a
                className="flex items-center gap-2 font-medium break-all"
                href={emailLink}
              >
                <FaEnvelope className="text-primary" />
                {settings.email}
              </a>
              {settings.address && (
                <p className="text-xs leading-relaxed text-ink/70">
                  {settings.address}
                </p>
              )}
              <div className="flex items-center gap-4 pt-2 text-lg text-primary">
                <a
                  href={settings.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="transition hover:text-primary/70"
                >
                  <FaFacebookF />
                </a>
                <a
                  href={settings.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="transition hover:text-primary/70"
                >
                  <FaInstagram />
                </a>
                <a
                  href={settings.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="transition hover:text-primary/70"
                >
                  <FaLinkedinIn />
                </a>
                <a
                  href={settings.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  className="transition hover:text-primary/70"
                >
                  <FaYoutube />
                </a>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;
